import { Params } from 'react-router-dom';
import {
    getCurrentFieldsOrFieldsSchema,
    getProjectDefaultAndCustomFieldsOrFieldsSchema,
    getProjectViewStructure,
} from '../redux/project/sliceProjectViewData';
import { getProjectDocStatus } from '../redux/project/sliceProjectDocStatus';
import { getProjectDocTypes } from '../redux/project/sliceProjectDocTypes';
import {
    IDocDefaultFields,
    IFieldConfig,
    clearDocuments,
    getProjectDocuments,
    paginationFinished,
} from '../redux/project/documents/sliceProjectDocuments';
import { getProjectFileTypes } from '../redux/project/sliceProjectFileTypes';
import { getProjectMetadata } from '../redux/project/sliceProjectMetadata';
import { getProjectSignatureStatus } from '../redux/project/sliceProjectSignatureStatus';
import { getProjectViewConfig } from '../redux/project/sliceProjectViews';
import { getProjectAreaConfig } from '../redux/project/sliceProjectAreas';
import {
    clearAdditionalDocuments,
    getAdditionalDocFieldSchema,
    getAdditionalDocFields,
} from '../redux/project/documents/sliceProjectAdditionalDocuments';
import { updateSelectedDocuments } from '../redux/project/documents/sliceProjectSelectedDocuments';
import { getProjectDocumentDetails } from '../redux/project/documents/sliceProjectDocumentDetails';
import { getProjectActionsConfig } from '../redux/project/sliceProjectActions';
import { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { abortMessage } from '../Constants';
import { logoutUser } from '../redux/user/sliceUserProfile';
import store from '../redux/store';

export const apiFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await fetch(input, init);

    if (response.status === 401) {
        store.dispatch(logoutUser());
    }

    return response;
};

/**
 * Creates a redirection URL in cases where the current URL is missing information to show/load data.
 * @param replicaId - Project replicaId.
 * @param areaName - (OPTIONAL) Current project area id.
 * @param viewName - (OPTIONAL) Current project view id.
 * @returns Redirection URL, or nothing if the URL should not yet be changed.
 */
export const getRedirectUrl = (replicaId: string, areaName?: string, viewName?: string) => {
    /** No replicaId - redirect to ProjectSelectionView. */
    if (!replicaId) {
        return '/';
    }

    /** Access data from redux store. */
    const { projectAreas, projectViews } = store.getState();
    const areas = projectAreas.data;
    const views = projectViews.data;

    /** View and/or area data missing - stop here. */
    if (!views || !areas) {
        return;
    }

    /** If the areaName doesn't exist or wasn't provided, load the first area defined for the project. */
    const areaId = areas[areaName]
        ? areaName
        : Object.keys(areas).sort((a, b) => areas[a].order - areas[b].order)[0];

    if (areaId === 'import') {
        return `/project/${replicaId}/import`;
    }

    /** Initially set viewId to the value of viewName (if provided). */
    let viewId = viewName;

    /** View does not exist. */
    if (!views[`${areaId}.${viewName}`]) {
        /** Find first view entry for the defined area. */
        const id = Object.keys(views)
            .filter(x => views[x].areaId === areaId)
            .sort((a, b) => views[a].order - views[b].order)[0];

        /** Update viewId. */
        viewId = views[id].viewId;
    }

    /** Build redirect URL and return. */
    return `/project/${replicaId}/${areaId}/${viewId}`;
};

export const getConfiguredFields = (fields: IFieldConfig[], fieldSchema: string[]): string[] => {
    if (!fields?.length || !fieldSchema?.length) {
        return [];
    }

    return [].concat(
        ...fieldSchema.map(fieldName => fields.find(x => x.field === fieldName)?.fields ?? []),
    );
};

/**
 * Loads project-specific data from backend storage.
 * @param replicaId - Target project replicaId.
 */
export const fetchProjectData = (replicaId: string) => {
    /** ReplicaId not provided - stop here. */
    if (!replicaId) {
        return;
    }

    store.dispatch(getProjectMetadata(replicaId));
    store.dispatch(getProjectAreaConfig(replicaId));
    store.dispatch(getProjectViewConfig(replicaId));
    store.dispatch(getProjectDocTypes(replicaId));
    store.dispatch(getProjectDocStatus(replicaId));
    store.dispatch(getProjectSignatureStatus(replicaId));
    store.dispatch(getProjectFileTypes(replicaId));
    store.dispatch(getAdditionalDocFields(replicaId));
    store.dispatch(getAdditionalDocFieldSchema(replicaId));
};

/**
 * Loads data that is specific for the view that the user is currently in.
 * @param params - URL parameters.
 */
export const fetchViewData = (params: Params<string>) => {
    /** Deconstruct params. */
    const { replicaId, areaName, viewName } = params;

    /** Access data from redux store. */
    const viewsData = store.getState().projectViews.data;

    /** Create view id. */
    const viewId = areaName === 'import' ? 'import.default' : `${areaName}.${viewName}`;

    if (areaName !== 'import') {
        store.dispatch(
            getProjectViewStructure({
                replicaId,
                viewName: viewsData[viewId].viewStructure,
                columnName: viewName,
            }),
        );
    }
    store.dispatch(
        getProjectDefaultAndCustomFieldsOrFieldsSchema({
            replicaId,
            viewName: viewId,
            requestKey: 'fields',
        }),
    );
    store.dispatch(
        getProjectDefaultAndCustomFieldsOrFieldsSchema({
            replicaId,
            viewName: viewId,
            requestKey: 'schema',
        }),
    );
    store.dispatch(getProjectActionsConfig(replicaId));
};

/**
 * Fetches documents located at the user's current project location.
 * @param params - URL parameters.
 */
export const fetchDocuments = async (params: Params<string>) => {
    /** Deconstruct params. */
    const { replicaId, category, areaName, viewName } = params;

    /** abort old request if new fetchdocument is send */
    if (getProjectDocumentsDispatch !== undefined) {
        getProjectDocumentsDispatch.abort(abortMessage);
    }

    /** Access data from redux store. */
    let state = store.getState();
    const viewsData = state.projectViews.data;
    const viewStructure = state.projectViewData.structure.data;
    const fields = getCurrentFieldsOrFieldsSchema('fields')(state);
    const fieldSchema = getCurrentFieldsOrFieldsSchema<string>('schema')(state);

    /** View structure and/or field schema data not loaded - stop here. */
    if (!viewStructure || !fields.length || !fieldSchema.length) {
        return;
    }
    store.dispatch(clearAdditionalDocuments());

    store.dispatch(clearDocuments());
    /** Determine if one of the view categories has the same id as the category id from the URL. */
    const categoryMatch = viewStructure.filter(x => x.id === category)[0];

    /** No category match, so clear the redux store's documents array and stop here. */
    if (!categoryMatch) {
        store.dispatch(clearDocuments());
        return;
    }

    /** load documents by "pagination" */
    let offset = 1;
    let limit = 500;

    do {
        /** Get the documents for the matching category. */
        getProjectDocumentsDispatch = store.dispatch(
            getProjectDocuments({
                replicaId,
                viewName: viewsData[`${areaName}.${viewName}`]?.viewDocuments,
                category: categoryMatch.category,
                offset,
                limit,
                fields: [].concat(
                    ...fieldSchema.map(uf => fields.filter(x => x.field === uf)[0]?.fields ?? []),
                ),
            }),
        );

        /** await lazy loading document request because notes needs more time handle them async */
        await getProjectDocumentsDispatch;

        state = store.getState();
        offset = offset + limit;
        limit = 1000; /** new limit for fast first loading & less requests */
    } while (offset < +categoryMatch.docCount && state.projectDocuments.runPagination);

    store.dispatch(paginationFinished());
};

/**
 * Resets the user's document selection if they change project or project area.
 * @param params - URL Parameters.
 */
export const resetDocSelection = (params: Params<string>) => {
    const { replicaId, areaName } = params;

    const { projectSelectedDocuments } = store.getState();
    const selectedDocuments = projectSelectedDocuments.storage;

    if (replicaId !== selectedDocuments.replicaId || areaName !== selectedDocuments.areaName) {
        store.dispatch(updateSelectedDocuments({ replicaId: '', areaName: '', documentIds: [] }));
    }
};

export const fetchViewDataAndDocuments = (params: Params<string>) => {
    fetchViewData(params);
    fetchDocuments(params);
};

/**
 * * Fetches document's details.
 * @param params URL Parameters.
 */
export const fetchDocumentDetails = (params: Params<string>) => {
    const { replicaId, universalId } = params;
    store.dispatch(getProjectFileTypes(replicaId));
    store.dispatch(getProjectDocTypes(replicaId));
    store.dispatch(getProjectDocStatus(replicaId));
    store.dispatch(
        getProjectDocumentDetails({
            replicaId,
            universalId,
        }),
    );
};

/**
 * global getProjectDocumentsDispatch variable for aborting lazy loading request
 */
let getProjectDocumentsDispatch: Promise<
    | PayloadAction<
          IDocDefaultFields[],
          string,
          {
              arg: {
                  replicaId: string;
                  viewName: string;
                  category: string;
                  fields: string[];
                  offset: number;
                  limit: number;
              };
              requestId: string;
              requestStatus: 'fulfilled';
          },
          never
      >
    | PayloadAction<
          unknown,
          string,
          {
              arg: {
                  replicaId: string;
                  viewName: string;
                  category: string;
                  fields: string[];
                  offset: number;
                  limit: number;
              };
              requestId: string;
              requestStatus: 'rejected';
              aborted: boolean;
              condition: boolean;
          } & (
              | { rejectedWithValue: true }
              | ({ rejectedWithValue: false } & Record<string, unknown>)
          ),
          SerializedError
      >
> & {
    abort: (reason?: string) => void;
    requestId: string;
    arg: {
        replicaId: string;
        viewName: string;
        category: string;
        fields: string[];
        offset: number;
        limit: number;
    };
    unwrap: () => Promise<IDocDefaultFields[]>;
};
